<!-- MarkObservationAsRead.vue -->
<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import { useNoteStore } from "@/stores/useNoteStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useTextStore } from "@/stores/useTextStore";
import { useUserStore } from "@/stores/useUserStore";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  showReadConfirmation: Boolean,
  noteId: Number,
  isViewingOnly: Boolean,
});

const emit = defineEmits(["close"]);

const noteStore = useNoteStore();
const activityStore = useActivityStore();
const textStore = useTextStore();
const userStore = useUserStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.showReadConfirmation,
  (val) => {
    if (val) resetPosition();
  }
);

const isSubmitting = ref(false);
const hasObservationText = ref(false);
const observationContent = ref("");
const observationType = ref("Observation");
const selectedTextContent = ref("");
const noteBody = ref("");
const isReadConfirmed = ref(false);
const isNoteRead = ref(false);

// Há algo a dar ciência? Certo/Errado são sinalizações explícitas e existem
// mesmo sem descrição preenchida (ver UpdateObservationAsync no backend), então
// a leitura precisa ser confirmável nesses casos. O texto vale sozinho por causa
// de notas antigas, que podem ter descrição com observationType nulo.
const hasObservationSignal = computed(
  () =>
    hasObservationText.value ||
    observationType.value === "Correct" ||
    observationType.value === "Incorrect"
);

const observationTypeClass = computed(() => {
  if (observationType.value === "Correct") return "type-green";
  if (observationType.value === "Incorrect") return "type-red";
  return "type-blue";
});

const isActivityOwner = computed(() => {
  const activityOwnerId = activityStore.selectedActivity?.createdByUserId;
  return (
    activityOwnerId != null &&
    Number(userStore.currentUser?.userId) === Number(activityOwnerId)
  );
});

const canConfirm = computed(
  () =>
    !props.isViewingOnly &&
    !isActivityOwner.value &&
    !isSubmitting.value &&
    isReadConfirmed.value &&
    hasObservationSignal.value
);

function closeModal() {
  if (!isSubmitting.value) emit("close");
}

/* ====================================================================
 ✅ TOAST (MESMO PADRÃO do AddCategoryForm.vue)
==================================================================== */
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function getUserFriendlyErrorMessage(error) {
  let userFriendlyMessage =
    "Ocorreu um erro ao processar a leitura da observação.";

  if (error?.response?.data?.errors) {
    const validationErrors = error.response.data.errors;
    const messages = [];
    for (const field in validationErrors) {
      if (validationErrors[field] && validationErrors[field].length > 0) {
        messages.push(validationErrors[field][0]);
      }
    }
    if (messages.length > 0) userFriendlyMessage = messages.join("\n");
  } else if (error?.response?.data?.message) {
    userFriendlyMessage = error.response.data.message;
  } else if (error?.message) {
    userFriendlyMessage = error.message;
  }

  return userFriendlyMessage;
}
/* ==================================================================== */

async function markAsRead() {
  if (!canConfirm.value) return;

  isSubmitting.value = true;

  try {
    await noteStore.markObservationAsRead(props.noteId);

    if (activityStore.idActivity) {
      await activityStore.getActivityById(activityStore.idActivity);
    }

    showSuccessToast("Observação marcada como lida com sucesso.");

    setTimeout(() => {
      isSubmitting.value = false;
      emit("close");
    }, 2400);
  } catch (error) {
    console.error("Erro detalhado da API:", error?.response?.data || error);
    showErrorToast(getUserFriendlyErrorMessage(error));
    isSubmitting.value = false;
  }
}

function handleEscKey(e) {
  if (e.key === "Escape") closeModal();
}

onMounted(async () => {
  document.addEventListener("keydown", handleEscKey);

  try {
    const data = await noteStore.getNoteById(props.noteId);

    observationContent.value = data?.observation || "";
    observationType.value = data?.observationType || "Observation";
    hasObservationText.value = !!data?.observation?.trim();
    isNoteRead.value = !!data?.isObservationRead;
    noteBody.value = data?.body || "";

    const txt = textStore.fileContent || "";
    const start = data?.textRangeStart ?? 0;
    const end = data?.textRangeEnd ?? 0;

    const slice = txt?.slice(start, end) || "";
    selectedTextContent.value =
      slice || "Não foi possível carregar o trecho selecionado da nota.";
  } catch (error) {
    console.error("Erro ao carregar nota:", error?.response?.data || error);
    showErrorToast(getUserFriendlyErrorMessage(error));
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);

  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
  }
});
</script>

<template>
  <div
    v-if="showReadConfirmation"
    class="modal-overlay"
    @click.self="closeModal"
  >
    <div ref="panelRef" class="text-card-modal-content" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">
          {{
            isViewingOnly
              ? "Visualizar Detalhes da Nota"
              : "Confirmar Leitura da Observação"
          }}
        </h3>

        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <form @submit.prevent="markAsRead" class="modal-form">
        <div class="card-content-grid">
          <div class="note-details-col">
            <label class="form-label text-label">Conteúdo da Nota</label>
            <blockquote class="note-body-blockquote">
              {{ noteBody || "A nota não possui conteúdo." }}
            </blockquote>

            <hr class="content-separator" />

            <div v-if="hasObservationSignal">
              <label class="form-label text-label">
                Observação do Revisor
                <span v-if="observationType === 'Correct'" class="observation-type-tag"
                  >(CERTO)</span
                >
                <span v-if="observationType === 'Incorrect'" class="observation-type-tag"
                  >(ERRADO)</span
                >
              </label>
              <blockquote
                class="note-body-blockquote note-observation-blockquote"
                :class="observationTypeClass"
              >
                <span v-if="hasObservationText">{{ observationContent }}</span>
                <!-- Certo/Errado sem descrição: a classificação é a mensagem -->
                <span v-else class="no-description">
                  O revisor apenas classificou a nota, sem escrever uma descrição.
                </span>
              </blockquote>
            </div>

            <div v-else class="empty-hint">
              Nenhuma observação foi adicionada para esta nota.
            </div>

            <div class="footer-info">
              <div v-if="isNoteRead" class="read-row read-row-disabled">
                <div class="read-text">Observação já marcada como lida</div>

                <label class="switch">
                  <input type="checkbox" checked disabled />
                  <span class="slider"></span>
                </label>
              </div>

              <div
                v-if="!isNoteRead && hasObservationSignal && !isViewingOnly"
                class="read-row"
                :class="{ 'read-row-disabled': isActivityOwner }"
              >
                <div
                  class="read-text"
                  :class="{ clickable: !isActivityOwner }"
                  @click="!isActivityOwner && (isReadConfirmed = !isReadConfirmed)"
                >
                  Estou ciente da observação
                </div>

                <label class="switch">
                  <input
                    type="checkbox"
                    v-model="isReadConfirmed"
                    :disabled="isSubmitting || isActivityOwner"
                  />
                  <span class="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div class="selected-text-col">
            <label class="form-label text-label">Texto selecionado na atividade</label>

            <blockquote
              class="themed-blockquote whitespace-pre-wrap break-words font-mono"
              :class="{ 'no-content': !selectedTextContent?.trim() }"
            >
              {{
                selectedTextContent?.trim() ||
                "Nenhum trecho de texto foi selecionado."
              }}
            </blockquote>

            <!-- ✅ AÇÕES: agora no mesmo padrão do EditNoteObservationForm -->
            <div class="actions-row">
              <!-- Secundário à esquerda -->
              <button
                type="button"
                @click="closeModal"
                class="btn btn-secondary"
                :disabled="isSubmitting"
              >
                Fechar
              </button>

              <!-- Primário à direita (só quando faz sentido) -->
              <button
                v-if="!isNoteRead && !isViewingOnly"
                type="submit"
                class="btn btn-primary-purple"
                :disabled="!canConfirm || isSubmitting"
              >
                <svg
                  v-if="isSubmitting"
                  class="w-5 h-5 mr-2 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>

                <svg
                  v-else
                  class="mr-1 -ml-1 w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                  />
                </svg>

                <span>{{
                  isSubmitting ? "Confirmando..." : "Confirmar Leitura"
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

.text-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  position: relative;
  box-shadow: var(--shadow-modal);

  margin-bottom: 0;
  overflow: hidden;

  max-height: calc(100vh - 40px);

  display: flex;
  flex-direction: column;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.card-content-grid {
  display: flex;
  padding: 1rem;
  gap: 1.25rem;

  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

/* =========================================================
   COLUNAS
========================================================= */
.note-details-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-right: 1rem;
  border-right: 1px solid var(--border-color);
  gap: 14px;
}

.selected-text-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.text-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--text-dark);
  margin-bottom: 0.3rem;
  display: block;
}

/* =========================================================
   BLOCKQUOTES
========================================================= */
.note-body-blockquote {
  white-space: pre-wrap;
  line-height: 1.5;
  margin-top: 5px;
  padding: 12px;
  border-left: 4px solid #cbd5e0;
  background-color: #f7fafc;
  border-radius: 4px;
  color: var(--text-dark);

  max-height: 260px;
  overflow-y: auto;
  overflow-x: hidden;
  word-break: break-word;
  scrollbar-gutter: stable;
}

.note-observation-blockquote.type-blue {
  border-left-color: var(--info);
  background-color: #e6f0ff;
  color: var(--text-dark);
}

.note-observation-blockquote.type-green {
  border-left-color: #38a169;
  background-color: #e6ffed;
  color: var(--text-dark);
}

.note-observation-blockquote.type-red {
  border-left-color: var(--danger);
  background-color: #ffe8e8;
  color: var(--text-dark);
}

.observation-type-tag {
  margin-left: 0.35rem;
  font-weight: 700;
  font-size: 0.82rem;
}

.themed-blockquote {
  background-color: var(--bg-header);
  border-left: 4px solid var(--brand-light);
  color: var(--text-dark);
  padding: 1rem;
  border-radius: 8px;
  line-height: 1.35;

  height: 420px;
  max-height: 420px;
  overflow-y: auto;
  box-sizing: border-box;
  font-size: 0.9rem;
}

.empty-hint {
  font-size: 0.9rem;
  color: var(--text-muted);
  font-style: italic;
  padding: 8px 0;
}

/* Classificação (certo/errado) sem descrição escrita */
.no-description {
  font-style: italic;
  opacity: 0.75;
}

/* =========================================================
   TOGGLE
========================================================= */
.footer-info {
  padding-top: 8px;
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 10px;
}

.read-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background-color: var(--surface-2);
}

.read-row-disabled {
  opacity: 0.65;
  pointer-events: none;
}

.read-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-dark);
}

.read-text.clickable {
  cursor: pointer;
}

.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: var(--neutral-400);
  transition: 0.2s;
  border-radius: 999px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 2px;
  top: 2px;
  background-color: var(--surface);
  transition: 0.2s;
  border-radius: 999px;
}

.switch input:checked + .slider {
  background-color: var(--success);
}

.switch input:checked + .slider:before {
  transform: translateX(20px);
}

/* =========================================================
   AÇÕES (mantém separador + “colado no fundo”)
========================================================= */
.actions-row {
  display: flex;
  justify-content: flex-end;
  gap: 10px;

  padding-top: 12px;
  border-top: 1px solid var(--border-color);

  margin-top: auto;
}

.btn-primary-purple {
  background-color: var(--brand);
  color: var(--text-on-brand);
  border-color: var(--brand);
  font-weight: 600;
}

.btn-primary-purple:hover:not(:disabled) {
  background-color: var(--brand-hover);
}

/* =========================================================
   RESPONSIVO
========================================================= */
@media (max-width: 900px) {
  .card-content-grid {
    flex-direction: column;
  }

  .note-details-col {
    border-right: none;
    padding-right: 0;
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 18px;
  }

  .themed-blockquote {
    height: 360px;
    max-height: 360px;
  }

  /* no mobile, melhor centralizar os botões */
  .actions-row {
    justify-content: center;
  }
}

</style>